import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { api } from "@/lib/api-client";
import { useAuthStore } from "@/store/auth-store";
import { useAppStore } from "@/store/store";
import { fromApiGuest, toApiGuestCreate, toApiGuestUpdate } from "@/lib/adapters";
import type { ApiGuest, PaginatedResponse } from "@/types/api";
import type { Guest } from "@/store/types";

const GUEST_FIELDS = [
  "id",
  "name",
  "phone",
  "email",
  "notes",
  "rating",
  "is_vip",
  "completed_count",
] as const satisfies readonly (keyof ApiGuest)[];

// ── Queries ──────────────────────────────────────────────────────

export function useGuests(search?: string) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const organizationId = useAuthStore((s) => s.organizationId);
  const locationId = useAuthStore((s) => s.locationId);

  const query = useQuery({
    queryKey: ["guests", organizationId, search],
    queryFn: async () => {
      const params = new URLSearchParams({ fields: GUEST_FIELDS.join(",") });
      if (search) params.set("search", search);
      const res = await api.get(`guests/?${params}`).json<PaginatedResponse<ApiGuest>>();
      return res.items.map(fromApiGuest);
    },
    enabled: isAuthenticated && !!organizationId && !!locationId,
  });

  // Sync to store so isGuestRisk and findGuest keep working
  useEffect(() => {
    if (query.data && !search) {
      useAppStore.setState({ guests: query.data });
    }
  }, [query.data, search]);

  return query;
}

/**
 * Paginated guest fetch for the clients page. Unlike ``useGuests`` (which
 * primes the store), this returns the raw paginated envelope so the table
 * can render ``count`` for "Mostrando X-Y de N" + page controls. ``search``
 * hits the same backend ``?search=`` filter.
 */
export function useGuestsPage(params: {
  search?: string;
  ratingBucket?: number;
  isUnrated?: boolean;
  isVip?: boolean;
  limit?: number;
  offset?: number;
}) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const organizationId = useAuthStore((s) => s.organizationId);
  const locationId = useAuthStore((s) => s.locationId);

  const limit = params.limit ?? 25;
  const offset = params.offset ?? 0;
  const { search, ratingBucket, isUnrated, isVip } = params;

  return useQuery({
    queryKey: [
      "guests-page",
      organizationId,
      search,
      ratingBucket,
      isUnrated,
      isVip,
      limit,
      offset,
    ],
    queryFn: async () => {
      const sp = new URLSearchParams({
        fields: GUEST_FIELDS.join(","),
        limit: String(limit),
        offset: String(offset),
      });
      if (search) sp.set("search", search);
      if (isUnrated) sp.set("is_unrated", "true");
      else if (ratingBucket !== undefined) sp.set("rating_bucket", String(ratingBucket));
      if (isVip !== undefined) sp.set("is_vip", String(isVip));
      const res = await api.get(`guests/?${sp}`).json<PaginatedResponse<ApiGuest>>();
      return {
        items: res.items.map(fromApiGuest),
        count: res.count,
        limit: res.limit,
        offset: res.offset,
      };
    },
    enabled: isAuthenticated && !!organizationId && !!locationId,
    // Keep the previous page visible while the next one loads — avoids the
    // table flashing back to a spinner on every page change.
    placeholderData: (prev) => prev,
  });
}

export function useGuest(id: string) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  return useQuery({
    queryKey: ["guests", id],
    queryFn: async () => {
      const params = new URLSearchParams({ fields: GUEST_FIELDS.join(",") });
      const res = await api.get(`guests/${id}/?${params}`).json<ApiGuest>();
      return fromApiGuest(res);
    },
    enabled: isAuthenticated && !!id,
  });
}

export interface GuestStats {
  reservationsCount: number;
  noShowCount: number;
  completedCount: number;
}

interface ApiGuestStats {
  id: string;
  reservations_count: number;
  no_show_count: number;
  completed_count: number;
}

/**
 * Per-guest reservation tallies from ``GET /api/guests/{id}/stats/``
 * (completed visits, no-shows, total). Used by the reservation detail
 * to surface the "X visitas · Y no-show" chips next to the name. Kept
 * separate from ``useGuest`` because the stats endpoint runs the
 * ``with_stats()`` annotation and the detail only needs the counts.
 */
export function useGuestStats(id: string | undefined) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  return useQuery({
    queryKey: ["guests", id, "stats"],
    queryFn: async () => {
      const res = await api.get(`guests/${id}/stats/`).json<ApiGuestStats>();
      return {
        reservationsCount: res.reservations_count,
        noShowCount: res.no_show_count,
        completedCount: res.completed_count,
      } satisfies GuestStats;
    },
    enabled: isAuthenticated && !!id,
    staleTime: 60_000,
  });
}

/**
 * Exact-match lookup against ``/api/guests/duplicates/?phone=`` on the
 * normalized phone. Returns the existing Guest that
 * ``Guest.objects.resolve_or_create`` would link to on create — so the modal
 * can warn the user *before* submitting that the phone they typed already
 * belongs to someone else. Email is not a dedupe key in this system.
 */
export function useGuestDuplicates(phone?: string) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const organizationId = useAuthStore((s) => s.organizationId);
  const locationId = useAuthStore((s) => s.locationId);

  const normalizedPhone = phone?.trim() ?? "";

  return useQuery({
    queryKey: ["guests-duplicates", organizationId, normalizedPhone],
    queryFn: async () => {
      const params = new URLSearchParams({
        phone: normalizedPhone,
        fields: GUEST_FIELDS.join(","),
      });
      const res = await api.get(`guests/duplicates/?${params}`).json<PaginatedResponse<ApiGuest>>();
      return res.items.map(fromApiGuest);
    },
    enabled: isAuthenticated && !!organizationId && !!locationId && normalizedPhone !== "",
  });
}

// ── Mutations ────────────────────────────────────────────────────

export function useCreateGuest() {
  const queryClient = useQueryClient();
  const organizationId = useAuthStore((s) => s.organizationId);

  return useMutation({
    mutationFn: (data: Partial<Guest>) =>
      api.post("guests/", { json: toApiGuestCreate(data) }).json<ApiGuest>(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["guests", organizationId] });
    },
  });
}

export function useUpdateGuest() {
  const queryClient = useQueryClient();
  const organizationId = useAuthStore((s) => s.organizationId);

  return useMutation({
    mutationFn: ({ id, changes }: { id: string; changes: Partial<Guest> }) =>
      api.patch(`guests/${id}/`, { json: toApiGuestUpdate(changes) }).json<ApiGuest>(),
    onSuccess: () => {
      // Both query keys feed off the same backend endpoint — invalidate them
      // together so the autocomplete in NewReservationModal and the paged
      // table on the clients page both pick up the edit.
      queryClient.invalidateQueries({ queryKey: ["guests", organizationId] });
      queryClient.invalidateQueries({ queryKey: ["guests-page", organizationId] });
    },
  });
}

export function useAnonymizeGuest() {
  const queryClient = useQueryClient();
  const organizationId = useAuthStore((s) => s.organizationId);

  return useMutation({
    mutationFn: (id: string) => api.post(`guests/${id}/anonymize/`).json<ApiGuest>(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["guests", organizationId] });
    },
  });
}
